// ── API client for SWA Functions ──

import type {
  SiteNode,
  CampaignNode,
  AreaSummary,
  InspectionDetail,
  PhotoResolved,
  TrendPoint,
  GeoCellSummary,
  DeterioratingAsset,
  AreaStatsResponse,
  EnrichedInspection,
  FullGraphResponse,
} from "../types/graph";

const BASE = "/api/graph";

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "same-origin", // SWA Entra cookie
    headers: { Accept: "application/json" },
    redirect: "manual", // Don't follow redirects — SWA returns 302 for unauthenticated API calls
  });

  // SWA returns 0 (opaque redirect) or 401 when not authenticated
  if (res.status === 401 || res.type === "opaqueredirect" || res.status === 0) {
    window.location.href =
      "/.auth/login/aad?post_login_redirect_uri=" +
      encodeURIComponent(window.location.pathname);
    throw new Error("Unauthorized — redirecting to login");
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

export const graphApi = {
  /** Top-level sites */
  getSites: () => fetchJson<SiteNode[]>("/sites"),

  /** Campaigns for a site */
  getCampaigns: (siteId: string) =>
    fetchJson<CampaignNode[]>(`/site/${encodeURIComponent(siteId)}`),

  /** Areas for a campaign */
  getAreas: (campaignId: string) =>
    fetchJson<AreaSummary[]>(`/campaign/${encodeURIComponent(campaignId)}`),

  /** Inspections for an area in a campaign */
  getInspections: (campaignId: string, areaName: string) =>
    fetchJson<InspectionDetail[]>(
      `/area/${encodeURIComponent(campaignId)}/${encodeURIComponent(areaName)}`,
    ),

  /** Photos for a record */
  getPhotos: (recordId: string) =>
    fetchJson<PhotoResolved[]>(`/photos/${encodeURIComponent(recordId)}`),

  /** Severity trend for a site */
  getTrend: (siteId: string) =>
    fetchJson<TrendPoint[]>(`/trend/${encodeURIComponent(siteId)}`),

  /** Geo-cell heatmap for a campaign */
  getGeoCells: (campaignId: string) =>
    fetchJson<GeoCellSummary[]>(`/geo/${encodeURIComponent(campaignId)}`),

  /** Deteriorating assets for a campaign */
  getDeteriorating: (campaignId: string) =>
    fetchJson<DeterioratingAsset[]>(
      `/deteriorating/${encodeURIComponent(campaignId)}`,
    ),

  /** Semantic relationships (SAME_DEFECT, SAME_ASSET, NEAR, PRECEDED_BY) between inspections */
  getRelationships: (inspectionIds: string[]) =>
    fetchJson<Array<{ source: string; target: string; type: string; weight?: number }>>(
      `/relationships?ids=${inspectionIds.join(",")}`,
    ),

  /** Enriched single-inspection detail with previous inspection comparison */
  getInspection: (inspectionId: string) =>
    fetchJson<EnrichedInspection>(
      `/inspection/${encodeURIComponent(inspectionId)}`,
    ),

  /** Area-level aggregate stats (severity distribution, top categories/elements) */
  getAreaStats: (campaignId: string, areaName: string) =>
    fetchJson<AreaStatsResponse>(
      `/area-stats/${encodeURIComponent(campaignId)}/${encodeURIComponent(areaName)}`,
    ),

  /** Full site graph in one query (sites → campaigns → areas → inspections + relationships) */
  getFullGraph: (siteId: string) =>
    fetchJson<FullGraphResponse>(`/full/${encodeURIComponent(siteId)}`),
};
